// Copyright 2026 The Simlin Authors. All rights reserved.
// Use of this source code is governed by the Apache License,
// Version 2.0, that can be found in the LICENSE file.

// Functional core for the Results band (MVP WS2): shape simulation series into
// the ChartSeries the reused LineChart expects, and compute a y-domain. Pure and
// engine-agnostic (plain array-likes) so it unit-tests without a live model.

import type { ChartSeries } from './LineChart';

/** Minimal, engine-agnostic view of a simulation output series. */
export interface SeriesLike {
  name: string;
  time: ArrayLike<number>;
  values: ArrayLike<number>;
}

/**
 * Convert simulation series to LineChart's ChartSeries (time -> x, value -> y),
 * assigning palette colors round-robin, and compute a y-domain that always
 * includes zero (so behavior-over-time reads against a stable baseline). Series
 * are truncated to the shorter of time/values to stay defensive against a
 * partial run.
 *
 * When `dashes` is supplied (WS8a Results palette), each full pass through the
 * color list advances one dash cycle -- so series past the palette length reuse
 * the hues with a different line style and stay distinguishable.
 */
export function toChartSeries(
  seriesList: readonly SeriesLike[],
  colors: readonly string[],
  dashes?: readonly (string | undefined)[],
): { series: ChartSeries[]; yDomain: [number, number] } {
  let yMin = 0;
  let yMax = 0;
  const series: ChartSeries[] = seriesList.map((s, i) => {
    const n = Math.min(s.time.length, s.values.length);
    const points: Array<{ x: number; y: number }> = [];
    for (let j = 0; j < n; j++) {
      const y = s.values[j];
      points.push({ x: s.time[j], y });
      if (y < yMin) {
        yMin = y;
      }
      if (y > yMax) {
        yMax = y;
      }
    }
    const dash =
      dashes && dashes.length > 0
        ? dashes[Math.floor(i / colors.length) % dashes.length]
        : undefined;
    return { name: s.name, color: colors[i % colors.length] ?? '#000000', dash, points };
  });
  return { series, yDomain: [Math.floor(yMin), Math.ceil(yMax)] };
}
